import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { Container, Row, Col, Card, Form, Button, Spinner, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUser, 
  faSave, 
  faTimes, 
  faEdit, 
  faImage, 
  faBuilding,
  faEnvelope,
  faPhone,
  faGlobe,
  faMapMarkerAlt,
  faIdCard,
  faCity,
  faMapPin
} from '@fortawesome/free-solid-svg-icons';
import { GlobalContext } from '../../../contexts/GlobalContext';
import { useProviders, updateSupplierProfile, getCountries, getStatesByCountry } from '../../../hooks/api/clientsProvides';
import TableDashboardStyle from '../../../assets/css/components/admin/products/TableDashboardStyle.css.js';
import HeadHeaderStyle from '../../../assets/css/components/admin/dashboard/dashboard-head-header/HeaderStyle.css.js';

const SupplierProfile = () => {
  const globalContext = useContext(GlobalContext);
  const { providersDashboardData } = useProviders();
  
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Referencias para cleanup
  const isMountedRef = useRef(true);
  const fileReaderRef = useRef(null);
  const successTimeoutRef = useRef(null);
  const countriesLoadedRef = useRef(false);
  
  const [formData, setFormData] = useState({
    name: '',
    doc: '',
    phone: '',
    web_site: '',
    email: '',
    street: '',
    street2: '',
    city: '',
    stateId: undefined,
    stateName: '',
    zip: '',
    countryId: undefined,
    countryName: '',
    photo: ''
  });
  
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [loadingStates, setLoadingStates] = useState(false);
  
  // Cleanup effect para manejar desmontaje del componente
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      // Limpiar FileReader si está activo
      if (fileReaderRef.current) {
        fileReaderRef.current.abort();
      }
      // Limpiar timeout de success
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
      }
    };
  }, []);

  // Función para cargar países
  const loadCountries = useCallback(async () => {
    setLoadingCountries(true);
    try {
      const entId = globalContext.currentEnterpriseId || "5f1378dcef2f752aeda16673";
      const response = await getCountries(entId);
      if (response && response.data && response.data.data && response.data.data.countries) {
        setCountries(response.data.data.countries);
      }
    } catch (error) {
      console.error('Error al cargar países:', error);
      setError('Error al cargar la lista de países');
    } finally {
      setLoadingCountries(false);
    }
  }, [globalContext.currentEnterpriseId]);
  
  // Función para cargar estados de un país
  const loadStates = useCallback(async (countryId) => {
    if (!countryId) {
      setStates([]);
      return;
    }
    
    setLoadingStates(true);
    try {
      const entId = globalContext.currentEnterpriseId || "5f1378dcef2f752aeda16673";
      const response = await getStatesByCountry(entId, countryId);
      if (response && response.data && response.data.data && response.data.data.states) {
        setStates(response.data.data.states);
      }
    } catch (error) {
      console.error('Error al cargar estados:', error);
      setError('Error al cargar la lista de estados');
    } finally {
      setLoadingStates(false);
    }
  }, [globalContext.currentEnterpriseId]);

  // Cargar países solo una vez al montar el componente
  useEffect(() => {
    if (!countriesLoadedRef.current) {
      countriesLoadedRef.current = true;
      loadCountries();
    }
  }, [loadCountries]);

  // Cargar datos iniciales del proveedor
  useEffect(() => {
    if (providersDashboardData) {
      console.log('=== DEBUG: Cargando datos del proveedor ===');
      console.log('providersDashboardData completo:', providersDashboardData);
      console.log('providersDashboardData._id:', providersDashboardData._id);
      console.log('providersDashboardData.id:', providersDashboardData.id);
      
      setFormData({
        name: providersDashboardData.name || providersDashboardData.ownerName || '',
        doc: providersDashboardData.doc || '',
        phone: providersDashboardData.phone || '',
        web_site: providersDashboardData.web_site || '',
        email: providersDashboardData.email || '',
        street: providersDashboardData.street || '',
        street2: providersDashboardData.street2 || '',
        city: providersDashboardData.city || '',
        stateId: providersDashboardData.stateId,
        stateName: providersDashboardData.stateName || '',
        zip: providersDashboardData.zip || '',
        countryId: providersDashboardData.countryId,
        countryName: providersDashboardData.countryName || '',
        photo: providersDashboardData.photo || ''
      });
      
      // Si hay una URL de foto, establecerla como preview
      if (providersDashboardData.photoUrl) {
        setPhotoPreview(providersDashboardData.photoUrl);
      } else if (providersDashboardData.photo) {
        setPhotoPreview(providersDashboardData.photo);
      }
      
      // Limpiar photoFile al cargar datos iniciales (no hay archivo nuevo seleccionado)
      setPhotoFile(null);
      
      // Si hay un país seleccionado, cargar sus estados
      if (providersDashboardData.countryId) {
        loadStates(providersDashboardData.countryId);
      }
    }
  }, [providersDashboardData, loadStates]);
  
  // Manejar cambio de país
  const handleCountryChange = (e) => {
    const parsedValue = parseInt(e.target.value);
    const countryId = e.target.value && !Number.isNaN(parsedValue) ? parsedValue : undefined;
    const selectedCountry = countries.find(c => c.id === countryId);
    
    setFormData(prev => ({
      ...prev,
      countryId: countryId,
      countryName: selectedCountry ? selectedCountry.name : '',
      stateId: undefined, // Limpiar estado cuando cambia el país
      stateName: ''
    }));
    
    // Cargar estados del país seleccionado
    if (countryId) {
      loadStates(countryId);
    } else {
      setStates([]);
    }
  };
  
  // Manejar cambio de estado
  const handleStateChange = (e) => {
    const parsedValue = parseInt(e.target.value);
    const stateId = e.target.value && !Number.isNaN(parsedValue) ? parsedValue : undefined;
    const selectedState = states.find(s => s.id === stateId);
    
    setFormData(prev => ({
      ...prev,
      stateId: stateId,
      stateName: selectedState ? selectedState.name : ''
    }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validar tipo de archivo
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setError('Formato de imagen no válido. Use JPEG, PNG, GIF o WebP');
      return;
    }

    // Validar tamaño (máximo 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      setError('La imagen es demasiado grande. Máximo 5MB');
      return;
    }

    setPhotoFile(file);
    setError(null);

    // Abortar FileReader anterior si existe
    if (fileReaderRef.current) {
      fileReaderRef.current.abort();
    }

    // Convertir a base64 para preview
    const reader = new FileReader();
    fileReaderRef.current = reader;
    
    reader.onloadend = () => {
      // Verificar si el componente sigue montado
      if (!isMountedRef.current) return;
      
      const base64 = reader.result;
      setPhotoPreview(base64);
      setFormData(prev => ({
        ...prev,
        photo: base64
      }));
    };
    
    reader.onerror = () => {
      if (!isMountedRef.current) return;
      setError('Error al leer la imagen');
    };
    
    reader.readAsDataURL(file);
  };

  const handleCancel = () => {
    // Restaurar datos originales
    if (providersDashboardData) {
      setFormData({
        name: providersDashboardData.name || providersDashboardData.ownerName || '',
        doc: providersDashboardData.doc || '',
        phone: providersDashboardData.phone || '',
        web_site: providersDashboardData.web_site || '',
        email: providersDashboardData.email || '',
        street: providersDashboardData.street || '',
        street2: providersDashboardData.street2 || '',
        city: providersDashboardData.city || '',
        stateId: providersDashboardData.stateId,
        stateName: providersDashboardData.stateName || '',
        zip: providersDashboardData.zip || '',
        countryId: providersDashboardData.countryId,
        countryName: providersDashboardData.countryName || '',
        photo: providersDashboardData.photo || ''
      });
      
      if (providersDashboardData.photoUrl) {
        setPhotoPreview(providersDashboardData.photoUrl);
      } else if (providersDashboardData.photo) {
        setPhotoPreview(providersDashboardData.photo);
      }
      
      // Restaurar estados si hay un país seleccionado
      if (providersDashboardData.countryId) {
        loadStates(providersDashboardData.countryId);
      } else {
        setStates([]);
      }
    }
    // Limpiar photoFile al cancelar (no hay archivo nuevo seleccionado)
    setPhotoFile(null);
    setIsEditing(false);
    setError(null);
    setSuccess(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Validar que el userId esté presente
      const userId = localStorage.getItem('_id');
      if (!userId || userId.trim() === '') {
        console.error('Error: userId no encontrado en localStorage');
        throw new Error('No se encontró el ID del usuario. Por favor, inicia sesión nuevamente.');
      }
      
      // Obtener entId del contexto o usar el valor por defecto
      const entId = globalContext.currentEnterpriseId || "5f1378dcef2f752aeda16673";
      
      // Obtener supplierId - puede estar en _id o en otro campo
      const supplierId = providersDashboardData._id || providersDashboardData.id;
      
      console.log('=== DEBUG: Información para actualizar perfil ===');
      console.log('userId:', userId);
      console.log('entId:', entId);
      console.log('supplierId:', supplierId);
      console.log('providersDashboardData:', providersDashboardData);
      console.log('providersDashboardData._id:', providersDashboardData._id);
      console.log('providersDashboardData.id:', providersDashboardData.id);
      console.log('globalContext.currentEnterpriseId:', globalContext.currentEnterpriseId);

      if (!supplierId) {
        console.error('No se encontró el ID del proveedor. Datos disponibles:', providersDashboardData);
        throw new Error('No se encontró el ID del proveedor. Por favor, recarga la página.');
      }
      
      if (!entId) {
        console.error('No se encontró el ID de la empresa');
        throw new Error('No se encontró el ID de la empresa. Por favor, recarga la página.');
      }

      // Función auxiliar para normalizar valores (undefined/null a cadena vacía para comparación)
      const normalize = (val) => val ?? '';
      
      // Función para comparar valores considerando undefined/null
      const hasChanged = (formVal, origVal) => normalize(formVal) !== normalize(origVal);
      
      // Preparar datos para enviar (solo campos modificados)
      const updateData = {};
      
      // Comparar name con el valor original (puede ser name o ownerName)
      const originalName = providersDashboardData.name || providersDashboardData.ownerName || '';
      if (hasChanged(formData.name, originalName)) {
        updateData.name = formData.name;
      }
      if (hasChanged(formData.doc, providersDashboardData.doc)) {
        updateData.doc = formData.doc;
      }
      if (hasChanged(formData.phone, providersDashboardData.phone)) {
        updateData.phone = formData.phone;
      }
      if (hasChanged(formData.web_site, providersDashboardData.web_site)) {
        updateData.web_site = formData.web_site;
      }
      if (hasChanged(formData.email, providersDashboardData.email)) {
        updateData.email = formData.email;
      }
      // Campos de dirección separados
      if (hasChanged(formData.street, providersDashboardData.street)) {
        updateData.street = formData.street;
      }
      if (hasChanged(formData.street2, providersDashboardData.street2)) {
        updateData.street2 = formData.street2;
      }
      if (hasChanged(formData.city, providersDashboardData.city)) {
        updateData.city = formData.city;
      }
      if (hasChanged(formData.zip, providersDashboardData.zip)) {
        updateData.zip = formData.zip;
      }
      // Para countryId y stateId, comparar con normalización especial para números/undefined
      if (formData.countryId !== providersDashboardData.countryId) {
        updateData.countryId = formData.countryId;
        updateData.countryName = formData.countryName;
      }
      if (formData.stateId !== providersDashboardData.stateId) {
        updateData.stateId = formData.stateId;
        updateData.stateName = formData.stateName;
      }
      // Solo enviar la foto si el usuario seleccionó un nuevo archivo
      // photoFile solo se establece cuando el usuario selecciona un archivo nuevo
      if (photoFile) {
        updateData.photo = formData.photo;
      }

      // Verificar que hay al menos un campo para actualizar
      if (Object.keys(updateData).length === 0) {
        setError('No hay cambios para guardar');
        setLoading(false);
        return;
      }

      const response = await updateSupplierProfile(entId, supplierId, updateData);

      if (response && response.data) {
        setSuccess('Perfil actualizado exitosamente');
        setIsEditing(false);
        
        // Actualizar el contexto global con los nuevos datos
        if (response.data.data && response.data.data.supplier) {
          const updatedSupplier = {
            ...providersDashboardData,
            ...response.data.data.supplier,
            ownerName: response.data.data.supplier.name,
            photoUrl: response.data.data.supplier.photoUrl
          };
          globalContext.setProvidersDashboardData(updatedSupplier);
          
          // Actualizar el preview de la foto si se subió una nueva
          if (photoFile && response.data.data.supplier.photoUrl) {
            setPhotoPreview(response.data.data.supplier.photoUrl);
          }
        }
        
        // Limpiar photoFile después de guardar exitosamente
        setPhotoFile(null);
        
        // Limpiar timeout anterior si existe
        if (successTimeoutRef.current) {
          clearTimeout(successTimeoutRef.current);
        }
        
        // Limpiar mensaje de éxito después de 5 segundos
        successTimeoutRef.current = setTimeout(() => {
          if (isMountedRef.current) {
            setSuccess(null);
          }
        }, 5000);
      }
    } catch (err) {
      console.error('Error al actualizar perfil:', err);
      setError(err.response?.data?.message || err.message || 'Error al actualizar el perfil');
    } finally {
      setLoading(false);
    }
  };

  if (!providersDashboardData) {
    return (
      <TableDashboardStyle>
        <Container fluid>
          <Row>
            <Col xs={12}>
              <Card className="border-0 shadow-sm">
                <Card.Body className="text-center p-4">
                  <p className="text-muted">No hay información del proveedor disponible</p>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </TableDashboardStyle>
    );
  }

  return (
    <TableDashboardStyle>
      <Container fluid>
        <Row>
          <Col xs={12}>
            <HeadHeaderStyle>
              <div className="d-flex justify-content-between align-items-start w-100">
                <div className="flex-grow-1">
                  <p className="text-start mb-2">Bienvenido a <strong>E-kommers</strong></p>
                  <div className="head-content d-flex justify-content-center align-items-center">
                    <div className="welcome-text text-center">
                      <h4 className="text-center fw-bold mb-1" style={{fontWeight: 'bold'}}>
                        Perfil del Proveedor
                        {providersDashboardData && (
                          <span className="text-primary"> - {providersDashboardData.ownerName || providersDashboardData.name}</span>
                        )}
                      </h4>
                      <p className="text-muted mb-0" style={{fontSize: '15px'}}>
                        Visualiza y edita la información de tu perfil
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </HeadHeaderStyle>
          </Col>
        </Row>

        <Row className="mt-4">
          <Col xs={12} lg={10} xl={9} className="mx-auto">
            {/* Card Principal */}
            <Card className="border-0 shadow-lg" style={{ borderRadius: '16px', overflow: 'hidden' }}>
              <Card.Header 
                style={{ 
                  background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                  borderBottom: '1px solid #dee2e6',
                  padding: '1.5rem'
                }}
              >
                <div className="d-flex justify-content-between align-items-center">
                  <div className="d-flex align-items-center">
                    <div 
                      style={{
                        backgroundColor: '#007bff',
                        color: 'white',
                        borderRadius: '50%',
                        width: '48px',
                        height: '48px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginRight: '15px',
                        boxShadow: '0 4px 12px rgba(0, 123, 255, 0.3)'
                      }}
                    >
                      <FontAwesomeIcon icon={faUser} size="lg" />
                    </div>
                    <div>
                      <h5 className="mb-0 fw-bold" style={{ color: '#333', fontSize: '1.25rem' }}>
                        Información del Perfil
                      </h5>
                      <p className="mb-0 text-muted small">Gestiona tu información personal y de contacto</p>
                    </div>
                  </div>
                  {!isEditing && (
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => setIsEditing(true)}
                      style={{
                        borderRadius: '8px',
                        padding: '0.5rem 1.25rem',
                        fontWeight: '600',
                        boxShadow: '0 2px 8px rgba(0, 123, 255, 0.2)',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 123, 255, 0.3)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 123, 255, 0.2)';
                      }}
                    >
                      <FontAwesomeIcon icon={faEdit} className="mr-2" />
                      Editar Perfil
                    </Button>
                  )}
                </div>
              </Card.Header>
              
              <Card.Body className="p-4" style={{ backgroundColor: '#ffffff', paddingBottom: '2rem' }}>
                {error && (
                  <Alert variant="danger" dismissible onClose={() => setError(null)}>
                    {error}
                  </Alert>
                )}
                
                {success && (
                  <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
                    {success}
                  </Alert>
                )}

                <Form onSubmit={handleSubmit}>
                  {/* Sección: Foto del Proveedor */}
                  <div 
                    className="mb-3 text-center"
                    style={{
                      padding: '1.25rem',
                      backgroundColor: '#f8f9fc',
                      borderRadius: '12px',
                      border: '1px solid #e9ecef'
                    }}
                  >
                    <div className="mb-3 position-relative d-inline-block">
                      {photoPreview ? (
                        <img
                          src={photoPreview}
                          alt="Foto del proveedor"
                          style={{
                            width: '160px',
                            height: '160px',
                            objectFit: 'cover',
                            borderRadius: '50%',
                            border: '4px solid #007bff',
                            boxShadow: '0 8px 24px rgba(0, 123, 255, 0.2)',
                            transition: 'all 0.3s ease'
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            width: '160px',
                            height: '160px',
                            borderRadius: '50%',
                            background: 'linear-gradient(135deg, #e9ecef 0%, #dee2e6 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: '0 auto',
                            border: '4px solid #dee2e6',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                          }}
                        >
                          <FontAwesomeIcon icon={faUser} size="4x" className="text-muted" />
                        </div>
                      )}
                    </div>
                    {isEditing && (
                      <div style={{ maxWidth: '400px', margin: '0 auto' }}>
                        <Form.Group>
                          <Form.Label className="fw-semibold d-flex align-items-center justify-content-center mb-2">
                            <FontAwesomeIcon icon={faImage} className="mr-2 text-primary" />
                            Cambiar Foto
                          </Form.Label>
                          <Form.Control
                            type="file"
                            accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                            onChange={handlePhotoChange}
                            style={{
                              borderRadius: '8px',
                              padding: '0.5rem',
                              border: '2px dashed #dee2e6',
                              transition: 'all 0.3s ease'
                            }}
                            onFocus={(e) => {
                              e.currentTarget.style.borderColor = '#007bff';
                              e.currentTarget.style.backgroundColor = '#f8f9ff';
                            }}
                            onBlur={(e) => {
                              e.currentTarget.style.borderColor = '#dee2e6';
                              e.currentTarget.style.backgroundColor = '#fff';
                            }}
                          />
                          <Form.Text className="text-muted small d-block mt-2">
                            Formatos: JPEG, PNG, GIF, WebP. Máximo 5MB
                          </Form.Text>
                        </Form.Group>
                      </div>
                    )}
                  </div>

                  {/* Sección: Información Básica */}
                  <div className="mb-3">
                    <div className="d-flex align-items-center mb-3">
                      <h5 className="mb-0 fw-bold" style={{ color: '#333', fontWeight: 'bold' }}>
                        Información Básica
                      </h5>
                    </div>
                    
                    <Row>

                      {/* Nombre */}
                      <Col xs={12} md={6} className="mb-3">
                        <Form.Group>
                          <Form.Label className="fw-semibold d-flex align-items-center mb-2" style={{gap: '10px'}}>
                            <FontAwesomeIcon icon={faBuilding} className="me-2 text-primary" size="sm" />
                            Nombre <span className="text-danger ms-1">*</span>
                          </Form.Label>
                          <Form.Control
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            disabled={!isEditing}
                            maxLength={200}
                            required
                            style={{
                              borderRadius: '8px',
                              padding: '0.75rem',
                              border: isEditing ? '2px solid #dee2e6' : '1px solid #e9ecef',
                              transition: 'all 0.3s ease',
                              fontSize: '0.95rem'
                            }}
                            onFocus={(e) => {
                              if (isEditing) {
                                e.currentTarget.style.borderColor = '#007bff';
                                e.currentTarget.style.boxShadow = '0 0 0 0.2rem rgba(0, 123, 255, 0.1)';
                              }
                            }}
                            onBlur={(e) => {
                              if (isEditing) {
                                e.currentTarget.style.borderColor = '#dee2e6';
                                e.currentTarget.style.boxShadow = 'none';
                              }
                            }}
                          />
                        </Form.Group>
                      </Col>

                      {/* Documento */}
                      <Col xs={12} md={6} className="mb-3">
                        <Form.Group>
                          <Form.Label className="fw-semibold d-flex align-items-center mb-2" style={{gap: '10px'}}>
                            <FontAwesomeIcon icon={faIdCard} className="me-2 text-primary" size="sm" />
                            Documento
                          </Form.Label>
                          <Form.Control
                            type="text"
                            name="doc"
                            value={formData.doc}
                            onChange={handleInputChange}
                            disabled={!isEditing}
                            maxLength={50}
                            style={{
                              borderRadius: '8px',
                              padding: '0.75rem',
                              border: isEditing ? '2px solid #dee2e6' : '1px solid #e9ecef',
                              transition: 'all 0.3s ease',
                              fontSize: '0.95rem'
                            }}
                            onFocus={(e) => {
                              if (isEditing) {
                                e.currentTarget.style.borderColor = '#007bff';
                                e.currentTarget.style.boxShadow = '0 0 0 0.2rem rgba(0, 123, 255, 0.1)';
                              }
                            }}
                            onBlur={(e) => {
                              if (isEditing) {
                                e.currentTarget.style.borderColor = '#dee2e6';
                                e.currentTarget.style.boxShadow = 'none';
                              }
                            }}
                          />
                        </Form.Group>
                      </Col>

                      {/* Email */}
                      <Col xs={12} md={6} className="mb-3">
                        <Form.Group>
                          <Form.Label className="fw-semibold d-flex align-items-center mb-2" style={{gap: '10px'}}>
                            <FontAwesomeIcon icon={faEnvelope} className="me-2 text-primary" size="sm" />
                            Correo Electrónico
                          </Form.Label>
                          <Form.Control
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            disabled={!isEditing}
                            maxLength={100}
                            style={{
                              borderRadius: '8px',
                              padding: '0.75rem',
                              border: isEditing ? '2px solid #dee2e6' : '1px solid #e9ecef',
                              transition: 'all 0.3s ease',
                              fontSize: '0.95rem'
                            }}
                            onFocus={(e) => {
                              if (isEditing) {
                                e.currentTarget.style.borderColor = '#007bff';
                                e.currentTarget.style.boxShadow = '0 0 0 0.2rem rgba(0, 123, 255, 0.1)';
                              }
                            }}
                            onBlur={(e) => {
                              if (isEditing) {
                                e.currentTarget.style.borderColor = '#dee2e6';
                                e.currentTarget.style.boxShadow = 'none';
                              }
                            }}
                          />
                        </Form.Group>
                      </Col>

                      {/* Teléfono Fijo */}
                      <Col xs={12} md={6} className="mb-3">
                        <Form.Group>
                          <Form.Label className="fw-semibold d-flex align-items-center mb-2" style={{gap: '10px'}}>
                            <FontAwesomeIcon icon={faPhone} className="me-2 text-primary" size="sm" />
                            Teléfono Fijo
                          </Form.Label>
                          <Form.Control
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleInputChange}
                            disabled={!isEditing}
                            maxLength={20}
                            style={{
                              borderRadius: '8px',
                              padding: '0.75rem',
                              border: isEditing ? '2px solid #dee2e6' : '1px solid #e9ecef',
                              transition: 'all 0.3s ease',
                              fontSize: '0.95rem'
                            }}
                            onFocus={(e) => {
                              if (isEditing) {
                                e.currentTarget.style.borderColor = '#007bff';
                                e.currentTarget.style.boxShadow = '0 0 0 0.2rem rgba(0, 123, 255, 0.1)';
                              }
                            }}
                            onBlur={(e) => {
                              if (isEditing) {
                                e.currentTarget.style.borderColor = '#dee2e6';
                                e.currentTarget.style.boxShadow = 'none';
                              }
                            }}
                          />
                        </Form.Group>
                      </Col>

                      {/* Sitio Web */}
                      <Col xs={12} md={6} className="mb-3">
                        <Form.Group>
                          <Form.Label className="fw-semibold d-flex align-items-center mb-2" style={{gap: '10px'}}>
                            <FontAwesomeIcon icon={faGlobe} className="me-2 text-primary" size="sm" />
                            Sitio Web
                          </Form.Label>
                          <Form.Control
                            type="url"
                            name="web_site"
                            value={formData.web_site}
                            onChange={handleInputChange}
                            disabled={!isEditing}
                            maxLength={200}
                            placeholder="https://www.ejemplo.com"
                            style={{
                              borderRadius: '8px',
                              padding: '0.75rem',
                              border: isEditing ? '2px solid #dee2e6' : '1px solid #e9ecef',
                              transition: 'all 0.3s ease',
                              fontSize: '0.95rem'
                            }}
                            onFocus={(e) => {
                              if (isEditing) {
                                e.currentTarget.style.borderColor = '#007bff';
                                e.currentTarget.style.boxShadow = '0 0 0 0.2rem rgba(0, 123, 255, 0.1)';
                              }
                            }}
                            onBlur={(e) => {
                              if (isEditing) {
                                e.currentTarget.style.borderColor = '#dee2e6';
                                e.currentTarget.style.boxShadow = 'none';
                              }
                            }}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                  </div>

                  {/* Sección: Dirección */}
                  <div className="mb-3">
                    <div className="d-flex align-items-center mb-3">
                      <h5 className="mb-0 fw-bold" style={{ color: '#333', fontWeight: 'bold' }}>
                        Dirección
                      </h5>
                    </div>
                    
                    <Row>

                      {/* Calle 1 */}
                      <Col xs={12} className="mb-3">
                        <Form.Group>
                          <Form.Label className="fw-semibold d-flex align-items-center mb-2" style={{gap: '10px'}}>
                            <FontAwesomeIcon icon={faMapMarkerAlt} className="me-2 text-primary" size="sm" />
                            Calle 1
                          </Form.Label>
                          <Form.Control
                            type="text"
                            name="street"
                            value={formData.street}
                            onChange={handleInputChange}
                            disabled={!isEditing}
                            maxLength={200}
                            placeholder="Av. Principal, Calle..."
                            style={{
                              borderRadius: '8px',
                              padding: '0.75rem',
                              border: isEditing ? '2px solid #dee2e6' : '1px solid #e9ecef',
                              transition: 'all 0.3s ease',
                              fontSize: '0.95rem'
                            }}
                            onFocus={(e) => {
                              if (isEditing) {
                                e.currentTarget.style.borderColor = '#007bff';
                                e.currentTarget.style.boxShadow = '0 0 0 0.2rem rgba(0, 123, 255, 0.1)';
                              }
                            }}
                            onBlur={(e) => {
                              if (isEditing) {
                                e.currentTarget.style.borderColor = '#dee2e6';
                                e.currentTarget.style.boxShadow = 'none';
                              }
                            }}
                          />
                        </Form.Group>
                      </Col>

                      {/* Calle 2 */}
                      <Col xs={12} className="mb-3">
                        <Form.Group>
                          <Form.Label className="fw-semibold d-flex align-items-center mb-2" style={{gap: '10px'}}>
                            <FontAwesomeIcon icon={faMapMarkerAlt} className="me-2 text-primary" size="sm" />
                            Calle 2
                          </Form.Label>
                          <Form.Control
                            type="text"
                            name="street2"
                            value={formData.street2}
                            onChange={handleInputChange}
                            disabled={!isEditing}
                            maxLength={200}
                            placeholder="Edificio, Piso, Oficina..."
                            style={{
                              borderRadius: '8px',
                              padding: '0.75rem',
                              border: isEditing ? '2px solid #dee2e6' : '1px solid #e9ecef',
                              transition: 'all 0.3s ease',
                              fontSize: '0.95rem'
                            }}
                            onFocus={(e) => {
                              if (isEditing) {
                                e.currentTarget.style.borderColor = '#007bff';
                                e.currentTarget.style.boxShadow = '0 0 0 0.2rem rgba(0, 123, 255, 0.1)';
                              }
                            }}
                            onBlur={(e) => {
                              if (isEditing) {
                                e.currentTarget.style.borderColor = '#dee2e6';
                                e.currentTarget.style.boxShadow = 'none';
                              }
                            }}
                          />
                        </Form.Group>
                      </Col>

                      {/* Ciudad y Código Postal */}
                      <Col xs={12} md={6} className="mb-3">
                        <Form.Group>
                          <Form.Label className="fw-semibold d-flex align-items-center mb-2" style={{gap: '10px'}}>
                            <FontAwesomeIcon icon={faCity} className="me-2 text-primary" size="sm" />
                            Ciudad
                          </Form.Label>
                          <Form.Control
                            type="text"
                            name="city"
                            value={formData.city}
                            onChange={handleInputChange}
                            disabled={!isEditing}
                            maxLength={100}
                            style={{
                              borderRadius: '8px',
                              padding: '0.75rem',
                              border: isEditing ? '2px solid #dee2e6' : '1px solid #e9ecef',
                              transition: 'all 0.3s ease',
                              fontSize: '0.95rem'
                            }}
                            onFocus={(e) => {
                              if (isEditing) {
                                e.currentTarget.style.borderColor = '#007bff';
                                e.currentTarget.style.boxShadow = '0 0 0 0.2rem rgba(0, 123, 255, 0.1)';
                              }
                            }}
                            onBlur={(e) => {
                              if (isEditing) {
                                e.currentTarget.style.borderColor = '#dee2e6';
                                e.currentTarget.style.boxShadow = 'none';
                              }
                            }}
                          />
                        </Form.Group>
                      </Col>

                      <Col xs={12} md={6} className="mb-3">
                        <Form.Group>
                          <Form.Label className="fw-semibold d-flex align-items-center mb-2" style={{gap: '10px'}}>
                            <FontAwesomeIcon icon={faMapPin} className="me-2 text-primary" size="sm" />
                            Código Postal
                          </Form.Label>
                          <Form.Control
                            type="text"
                            name="zip"
                            value={formData.zip}
                            onChange={handleInputChange}
                            disabled={!isEditing}
                            maxLength={20}
                            style={{
                              borderRadius: '8px',
                              padding: '0.75rem',
                              border: isEditing ? '2px solid #dee2e6' : '1px solid #e9ecef',
                              transition: 'all 0.3s ease',
                              fontSize: '0.95rem'
                            }}
                            onFocus={(e) => {
                              if (isEditing) {
                                e.currentTarget.style.borderColor = '#007bff';
                                e.currentTarget.style.boxShadow = '0 0 0 0.2rem rgba(0, 123, 255, 0.1)';
                              }
                            }}
                            onBlur={(e) => {
                              if (isEditing) {
                                e.currentTarget.style.borderColor = '#dee2e6';
                                e.currentTarget.style.boxShadow = 'none';
                              }
                            }}
                          />
                        </Form.Group>
                      </Col>

                      {/* País */}
                      <Col xs={12} md={6} className="mb-3">
                        <Form.Group>
                          <Form.Label className="fw-semibold d-flex align-items-center mb-2" style={{gap: '10px'}}>
                            <FontAwesomeIcon icon={faGlobe} className="me-2 text-primary" size="sm" />
                            País
                          </Form.Label>
                          <Form.Control
                            as="select"
                            name="countryId"
                            value={formData.countryId || ''}
                            onChange={handleCountryChange}
                            disabled={!isEditing || loadingCountries}
                            style={{
                              borderRadius: '8px',
                              padding: '0.75rem',
                              border: isEditing ? '2px solid #dee2e6' : '1px solid #e9ecef',
                              transition: 'all 0.3s ease',
                              fontSize: '0.95rem',
                              cursor: isEditing && !loadingCountries ? 'pointer' : 'not-allowed',
                              minHeight: '42px'
                            }}
                            onFocus={(e) => {
                              if (isEditing && !loadingCountries) {
                                e.currentTarget.style.borderColor = '#007bff';
                                e.currentTarget.style.boxShadow = '0 0 0 0.2rem rgba(0, 123, 255, 0.1)';
                              }
                            }}
                            onBlur={(e) => {
                              if (isEditing) {
                                e.currentTarget.style.borderColor = '#dee2e6';
                                e.currentTarget.style.boxShadow = 'none';
                              }
                            }}
                          >
                            <option value="">Seleccione un país</option>
                            {countries.map(country => (
                              <option key={country.id} value={country.id}>
                                {country.name}
                              </option>
                            ))}
                          </Form.Control>
                          {loadingCountries && (
                            <Form.Text className="text-muted small d-flex align-items-center mt-2">
                              <Spinner size="sm" className="me-2" />
                              Cargando países...
                            </Form.Text>
                          )}
                        </Form.Group>
                      </Col>

                      {/* Estado/Provincia */}
                      <Col xs={12} md={6} className="mb-3">
                        <Form.Group>
                          <Form.Label className="fw-semibold d-flex align-items-center mb-2" style={{gap: '10px'}}>
                            <FontAwesomeIcon icon={faMapMarkerAlt} className="me-2 text-primary" size="sm" />
                            Estado/Provincia
                          </Form.Label>
                          <Form.Control
                            as="select"
                            name="stateId"
                            value={formData.stateId || ''}
                            onChange={handleStateChange}
                            disabled={!isEditing || !formData.countryId || loadingStates}
                            style={{
                              borderRadius: '8px',
                              padding: '0.75rem',
                              border: isEditing ? '2px solid #dee2e6' : '1px solid #e9ecef',
                              transition: 'all 0.3s ease',
                              fontSize: '0.95rem',
                              cursor: isEditing && formData.countryId && !loadingStates ? 'pointer' : 'not-allowed',
                              backgroundColor: !formData.countryId ? '#f8f9fa' : 'white',
                              minHeight: '42px'
                            }}
                            onFocus={(e) => {
                              if (isEditing && formData.countryId && !loadingStates) {
                                e.currentTarget.style.borderColor = '#007bff';
                                e.currentTarget.style.boxShadow = '0 0 0 0.2rem rgba(0, 123, 255, 0.1)';
                              }
                            }}
                            onBlur={(e) => {
                              if (isEditing) {
                                e.currentTarget.style.borderColor = '#dee2e6';
                                e.currentTarget.style.boxShadow = 'none';
                              }
                            }}
                          >
                            <option value="">
                              {!formData.countryId 
                                ? 'Seleccione primero un país' 
                                : 'Seleccione un estado'}
                            </option>
                            {states.map(state => (
                              <option key={state.id} value={state.id}>
                                {state.name}
                              </option>
                            ))}
                          </Form.Control>
                          {formData.countryId && loadingStates && (
                            <Form.Text className="text-muted small d-flex align-items-center mt-2">
                              <Spinner size="sm" className="me-2" />
                              Cargando estados...
                            </Form.Text>
                          )}
                        </Form.Group>
                      </Col>
                    </Row>
                  </div>

                  {isEditing && (
                    <div 
                      className="d-flex justify-content-end mt-4 pt-3 "
                      style={{
                        borderTop: '2px solid #e9ecef',
                        paddingTop: '1.25rem',
                        gap: '20px'
                      }}
                    >
                      <Button
                        variant="outline-secondary"
                        onClick={handleCancel}
                        disabled={loading}
                        style={{
                          borderRadius: '8px',
                          padding: '0.625rem 1.5rem',
                          fontWeight: '600',
                          minWidth: '140px',
                          transition: 'all 0.3s ease',
                          borderWidth: '2px'
                        }}
                        onMouseEnter={(e) => {
                          if (!loading) {
                            e.currentTarget.style.transform = 'translateY(-2px)';
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(108, 117, 125, 0.2)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = 'none';
                        }}
                      >
                        Cancelar
                      </Button>
                      <Button
                        variant="primary"
                        type="submit"
                        disabled={loading}
                        style={{
                          borderRadius: '8px',
                          padding: '0.625rem 1.5rem',
                          fontWeight: '600',
                          minWidth: '180px',
                          boxShadow: '0 4px 12px rgba(0, 123, 255, 0.3)',
                          transition: 'all 0.3s ease',
                          border: 'none'
                        }}
                        onMouseEnter={(e) => {
                          if (!loading) {
                            e.currentTarget.style.transform = 'translateY(-2px)';
                            e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 123, 255, 0.4)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 123, 255, 0.3)';
                        }}
                      >
                        {loading ? (
                          <>
                            <Spinner
                              as="span"
                              animation="border"
                              size="sm"
                              role="status"
                              aria-hidden="true"
                              className="me-2"
                            />
                            Guardando...
                          </>
                        ) : (
                          <>
                            Guardar Cambios
                          </>
                        )}
                      </Button>
                    </div>
                  )}
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </TableDashboardStyle>
  );
};

export default SupplierProfile;

